import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";

const LOCKED = ["Accepted", "Paid"];

// Start/End times per line, shown on the approvals table.
export function TimesCell({ ts, onEdit }) {
  const lines = ts.entries || [];
  const any = lines.some(e => e.start_time || e.end_time);
  return (
    <div className="space-y-0.5 text-xs" data-testid={`ts-times-${ts.id}`}>
      {any ? lines.map((e, i) => (
        <div key={i} className="whitespace-nowrap">{e.start_time || "—"}–{e.end_time || "—"}</div>
      )) : <div className="opacity-40">—</div>}
      {!LOCKED.includes(ts.status) && <button className="hub-link" onClick={() => onEdit(ts)} data-testid={`btn-edit-times-${ts.id}`}>Edit times</button>}
    </div>
  );
}

function spanHours(s, e) {
  if (!s || !e) return null;
  const [sh, sm] = s.split(":").map(Number); const [eh, em] = e.split(":").map(Number);
  const m = (eh * 60 + em) - (sh * 60 + sm);
  return m < 0 ? -1 : Math.round(m / 15) / 4;
}

export function TimesEditor({ ts, onClose }) {
  const [rows, setRows] = useState((ts.entries || []).map(e => ({ start_time: e.start_time || "", end_time: e.end_time || "", hours: e.hours || 0 })));
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  useEffect(() => { ref.current?.scrollIntoView({ behavior: "smooth", block: "start" }); }, [ts.id]);
  const set = (i, k, v) => setRows(r => r.map((x, j) => (j === i ? { ...x, [k]: v } : x)));
  async function save() {
    const bad = rows.findIndex(r => spanHours(r.start_time, r.end_time) === -1);
    if (bad >= 0) { setErr(`End time is before start time (line ${bad + 1})`); return; }
    setBusy(true); setErr("");
    try {
      for (let i = 0; i < rows.length; i++) {
        const o = ts.entries[i]; const r = rows[i];
        if ((o.start_time || "") === r.start_time && (o.end_time || "") === r.end_time && Number(o.hours) === Number(r.hours)) continue;
        await api.patch(`/timesheets/${ts.id}/entries/${i}/times`, { start_time: r.start_time || null, end_time: r.end_time || null, hours: Number(r.hours) || 0 });
      }
      onClose(true);
    } catch (e) { setErr(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : "Couldn't save times"); setBusy(false); }
  }
  return (
    <div ref={ref} className="card space-y-2" data-testid="times-editor">
      <h3 className="text-silver">Edit times — {ts.worker_name}</h3>
      {rows.map((r, i) => {
        const h = spanHours(r.start_time, r.end_time);
        return (
          <div key={i} className="flex flex-wrap gap-2 items-center" data-testid={`times-row-${i}`}>
            <span className="text-sm min-w-[10rem]">{ts.entries[i].project_name || `Line ${i + 1}`}</span>
            <input type="time" className="input" style={{ width: "8rem" }} value={r.start_time} onChange={e => set(i, "start_time", e.target.value)} data-testid={`times-start-${i}`} aria-label="Start time" />
            <input type="time" className="input" style={{ width: "8rem" }} value={r.end_time} onChange={e => set(i, "end_time", e.target.value)} data-testid={`times-end-${i}`} aria-label="End time" />
            {h === null
              ? <input type="number" step="0.25" min="0" className="input" style={{ width: "6rem" }} value={r.hours} onChange={e => set(i, "hours", e.target.value)} data-testid={`times-hours-${i}`} aria-label="Hours" />
              : <span className={`text-sm ${h < 0 ? "text-red-400" : ""}`} data-testid={`times-calc-${i}`}>{h < 0 ? "End time is before start time" : `${h.toFixed(2)} h`}</span>}
          </div>
        );
      })}
      <div className="text-xs opacity-60">Hours are worked out to the nearest 0.25 h; the unpaid break is re-applied as now.</div>
      {err && <div className="text-sm text-red-400" data-testid="times-edit-error">{err}</div>}
      <div className="flex gap-2">
        <button className="btn-silver" disabled={busy} onClick={save} data-testid="btn-times-save">Save</button>
        <button className="btn-ghost" onClick={() => onClose(false)} data-testid="btn-times-cancel">Cancel</button>
      </div>
    </div>
  );
}
